import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
// eslint-disable-next-line import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { theme } from '@frontend/theme';
import { Avatar, NakedButton, Text } from '@frontend/design-system';
import { LightboxControls } from './lightbox-controls';
import { MediaLightbox } from './media-lightbox';

export type PdfPreviewProps = {
  src?: string;
  filename?: string;
  senderName?: string;
  timestamp?: string | Date;
  show: boolean;
  onClose: () => void;
  onDownload?: () => void;
};

// pdfjs-dist references DOMMatrix at module load, which breaks jsdom/happy-dom tests.
// Lazy-load it so importing this file (e.g. via the barrel) is test-safe.
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
const loadPdfjs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((mod) => {
      mod.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      return mod;
    });
  }
  return pdfjsPromise;
};

const THUMBNAIL_GAP = 12;
const RAIL_WIDTH = 'clamp(96px, 14vw, 200px)';

export const PdfPreview = ({ src, filename, senderName, timestamp, show, onClose, onDownload }: PdfPreviewProps) => {
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (show) {
      setRotation(0);
      setZoom(1);
      setCurrentPage(1);
    }
  }, [show, src]);

  useEffect(() => {
    if (!show || !src) return;
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    (async () => {
      const { getDocument } = await loadPdfjs();
      if (cancelled) return;
      const task = getDocument({ url: src });
      try {
        loaded = await task.promise;
      } catch {
        return;
      }
      if (cancelled) {
        loaded.destroy();
        return;
      }
      setDoc(loaded);
    })();
    return () => {
      cancelled = true;
      loaded?.destroy();
      setDoc(null);
    };
  }, [show, src]);

  const [resizeTick, setResizeTick] = useState(0);
  useEffect(() => {
    if (!mainContainerRef.current) return;
    const node = mainContainerRef.current;
    const observer = new ResizeObserver(() => setResizeTick((t) => t + 1));
    observer.observe(node);
    return () => observer.disconnect();
  }, [show, doc]);

  useEffect(() => {
    if (!doc || !mainCanvasRef.current || !mainContainerRef.current) return;
    const container = mainContainerRef.current;
    const canvas = mainCanvasRef.current;
    if (container.clientWidth === 0 || container.clientHeight === 0) {
      const raf = requestAnimationFrame(() => setResizeTick((t) => t + 1));
      return () => cancelAnimationFrame(raf);
    }
    let renderTask: { cancel: () => void } | null = null;
    let cancelled = false;
    (async () => {
      const page = await doc.getPage(currentPage);
      if (cancelled) return;
      const baseViewport = page.getViewport({ scale: 1, rotation });
      const fitScale = Math.min(
        container.clientWidth / baseViewport.width,
        container.clientHeight / baseViewport.height
      );
      const dpr = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: fitScale * zoom * dpr, rotation });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const task = page.render({ canvasContext: ctx, viewport, canvas });
      renderTask = task;
      try {
        await task.promise;
      } catch (err) {
        if (!(err instanceof Error) || err.name !== 'RenderingCancelledException') throw err;
      }
    })();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [doc, currentPage, rotation, zoom, resizeTick]);

  if (!src) return null;

  const handleOpenInNewWindow = () => {
    window.open(src, '_blank', 'noopener,noreferrer');
  };

  return (
    <MediaLightbox
      show={show}
      onClose={onClose}
      header={
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <Avatar name={senderName} size='small' />
          <div css={{ display: 'flex', flexDirection: 'column', marginLeft: theme.spacing(1) }}>
            <Text color='white' size='small'>
              {senderName ?? filename ?? 'Document'}
            </Text>
            {timestamp && (
              <Text color='white' size='small'>
                {timestamp.toString()}
              </Text>
            )}
          </div>
        </div>
      }
      footer={
        <LightboxControls
          onRotate={() => setRotation((r) => (r + 90) % 360)}
          zoom={{ value: zoom, onChange: setZoom, min: 1, max: 3, step: 0.25 }}
          onDownload={onDownload}
          onOpenInNewWindow={handleOpenInNewWindow}
        />
      }
    >
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          width: 'min(1200px, 92vw)',
          height: '85vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={mainContainerRef}
          css={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            overflow: 'hidden',
          }}
        >
          <canvas
            ref={mainCanvasRef}
            css={{
              maxWidth: '100%',
              borderRadius: theme.borderRadius.small,
              background: 'transparent',
            }}
          />
        </div>

        {doc && doc.numPages > 1 && (
          <div
            css={{
              width: RAIL_WIDTH,
              flexShrink: 0,
              overflowY: 'auto',
              scrollbarGutter: 'stable',
              padding: theme.spacing(2, 1, 2, 0.5),
              scrollPaddingTop: theme.spacing(2),
              scrollPaddingBottom: theme.spacing(2),
              background: 'transparent',
            }}
          >
            <ul
              css={{
                listStyle: 'none',
                margin: 0,
                padding: theme.spacing(0.5, 0),
                display: 'flex',
                flexDirection: 'column',
                gap: THUMBNAIL_GAP,
              }}
            >
              {Array.from({ length: doc.numPages }, (_, i) => i + 1).map((pageNumber) => (
                <li key={pageNumber}>
                  <Thumbnail
                    doc={doc}
                    pageNumber={pageNumber}
                    selected={pageNumber === currentPage}
                    onClick={() => setCurrentPage(pageNumber)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </MediaLightbox>
  );
};

type ThumbnailProps = {
  doc: PDFDocumentProxy;
  pageNumber: number;
  selected: boolean;
  onClick: () => void;
};

const Thumbnail = ({ doc, pageNumber, selected, onClick }: ThumbnailProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!buttonRef.current) return;
    const node = buttonRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const node = wrapperRef.current;
    const update = () => setWidth(node.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !canvasRef.current || width === 0) return;
    const canvas = canvasRef.current;
    let renderTask: { cancel: () => void } | null = null;
    let cancelled = false;
    (async () => {
      const page = await doc.getPage(pageNumber);
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const dpr = window.devicePixelRatio || 1;
      const scale = (width / base.width) * dpr;
      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.width = `${viewport.width / dpr}px`;
      canvas.style.height = `${viewport.height / dpr}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const task = page.render({ canvasContext: ctx, viewport, canvas });
      renderTask = task;
      try {
        await task.promise;
      } catch (err) {
        if (!(err instanceof Error) || err.name !== 'RenderingCancelledException') throw err;
      }
    })();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [visible, doc, pageNumber, width]);

  useEffect(() => {
    if (selected && buttonRef.current) {
      buttonRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selected]);

  return (
    <NakedButton
      ref={buttonRef}
      onClick={onClick}
      aria-label={`Go to page ${pageNumber}`}
      aria-current={selected ? 'page' : undefined}
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 0,
        width: '100%',
        borderRadius: theme.borderRadius.small,
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      <div
        ref={wrapperRef}
        css={{
          width: '100%',
          aspectRatio: '1 / 1.29', // letter-size placeholder until the canvas renders
          background: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <canvas
          ref={canvasRef}
          css={{
            display: 'block',
            // Selection ring sits flush against the actual page edges.
            boxShadow: selected ? `0 0 0 2px ${theme.colors.primary50}` : 'none',
            transition: 'box-shadow 0.15s ease',
          }}
        />
      </div>
    </NakedButton>
  );
};
